/**
 * Network monitoring for web (navigator.onLine) and Capacitor.
 *
 * Android's @capacitor/network plugin sets `connected` only when the link
 * has both INTERNET and NET_CAPABILITY_VALIDATED. Validation is a captive-
 * portal probe that fails on a weak or slow signal, so `connected` becomes
 * false while Wi-Fi or mobile data is still usable. `onLost` also reports
 * connectionType "none" for a brief dip. Treating either as offline used to
 * replace the whole app with the no-internet screen and reject every request.
 */

export const OFFLINE_CONFIRM_MS = 2500;

let networkListeners = new Set();
let currentStatus = readInitialStatus();
let offlineTimer = null;
let confirmGeneration = 0;
let cleanupNative = null;
let statusReader = readPluginStatus;

function readInitialStatus() {
  let native = false;
  try {
    native = Boolean(window.Capacitor?.isNativePlatform?.());
  } catch {
    native = false;
  }
  // On a handset, navigator.onLine follows the same validation bit and is
  // often false while the radio is up. Assume online until the plugin
  // confirms there is no transport.
  if (native) {
    return { connected: true, connectionType: "unknown" };
  }
  return readBrowserStatus();
}

function readBrowserStatus() {
  const online = typeof navigator === "undefined" ? true : navigator.onLine !== false;
  return {
    connected: online,
    connectionType: online ? "unknown" : "none",
  };
}

/**
 * A wifi / cellular / other transport is usable even when Android has not
 * validated it. Offline only when the plugin reports no transport.
 */
export function resolveReportedStatus(raw) {
  const type = raw?.connectionType || "unknown";
  if (type === "wifi" || type === "cellular" || type === "unknown") {
    return { connected: true, connectionType: type };
  }
  if (raw?.connected) {
    return { connected: true, connectionType: type === "none" ? "unknown" : type };
  }
  return { connected: false, connectionType: "none" };
}

function notifyListeners(status) {
  currentStatus = status;
  networkListeners.forEach((cb) => {
    try {
      cb(status);
    } catch {
      // one listener must not break the others
    }
  });
}

function publishIfChanged(status) {
  if (
    currentStatus.connected === status.connected &&
    currentStatus.connectionType === status.connectionType
  ) {
    return;
  }
  notifyListeners(status);
}

async function readPluginStatus() {
  const { Network } = await import("@capacitor/network");
  return Network.getStatus();
}

function clearOfflineTimer() {
  if (offlineTimer) {
    clearTimeout(offlineTimer);
    offlineTimer = null;
  }
  confirmGeneration += 1;
}

function confirmOffline(generation) {
  Promise.resolve()
    .then(() => statusReader())
    .then((raw) => {
      if (generation !== confirmGeneration) return;
      const confirmed = raw
        ? resolveReportedStatus(raw)
        : { connected: false, connectionType: "none" };
      publishIfChanged(confirmed);
    })
    .catch(() => {
      if (generation !== confirmGeneration) return;
      publishIfChanged({ connected: false, connectionType: "none" });
    });
}

/** Apply a plugin or browser report. Weak links stay online; true loss is confirmed. */
export function applyNetworkReport(raw) {
  const resolved = resolveReportedStatus(raw);
  if (resolved.connected) {
    clearOfflineTimer();
    publishIfChanged(resolved);
    return;
  }

  if (!currentStatus.connected) {
    publishIfChanged({ connected: false, connectionType: "none" });
    return;
  }

  if (offlineTimer) return;

  const generation = confirmGeneration;
  offlineTimer = setTimeout(() => {
    offlineTimer = null;
    confirmOffline(generation);
  }, OFFLINE_CONFIRM_MS);
}

/** Re-read the plugin immediately. Used by the offline screen retry button. */
export async function refreshNetworkStatus() {
  clearOfflineTimer();
  let raw = null;
  try {
    raw = await statusReader();
  } catch {
    raw = null;
  }
  const resolved = raw ? resolveReportedStatus(raw) : readBrowserStatus();
  notifyListeners(resolved);
  return currentStatus;
}

function handleBrowserOnline() {
  applyNetworkReport({ connected: true, connectionType: "unknown" });
}

function handleBrowserOffline() {
  applyNetworkReport({ connected: false, connectionType: "none" });
}

export async function initNetworkMonitor() {
  window.addEventListener("online", handleBrowserOnline);
  window.addEventListener("offline", handleBrowserOffline);

  try {
    const { Network } = await import("@capacitor/network");
    const status = await Network.getStatus();
    applyNetworkReport(status);

    const handler = await Network.addListener("networkStatusChange", (next) => {
      applyNetworkReport(next);
    });

    cleanupNative = () => handler.remove();
  } catch {
    // Web without the Capacitor plugin — the browser listeners above are enough.
  }
}

export function getNetworkStatus() {
  return currentStatus;
}

export function addNetworkListener(callback) {
  networkListeners.add(callback);
  return () => networkListeners.delete(callback);
}

export function cleanupNetworkMonitor() {
  clearOfflineTimer();
  cleanupNative?.();
  cleanupNative = null;
  window.removeEventListener("online", handleBrowserOnline);
  window.removeEventListener("offline", handleBrowserOffline);
  networkListeners.clear();
}

/** Test-only: replace the Capacitor status read. */
export function __setNetworkStatusReaderForTests(reader) {
  statusReader = typeof reader === "function" ? reader : readPluginStatus;
}

/** Test-only: drop listeners, timers, and restore the browser snapshot. */
export function __resetNetworkMonitorForTests() {
  cleanupNetworkMonitor();
  currentStatus = readBrowserStatus();
  statusReader = readPluginStatus;
}
